import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { currentLocale } from '../../i18n';
import TeamLogo from './TeamLogo';
import './Leagues.css';

/** Date-time format options for the "when" column; locale resolved per render. */
const WHEN_FMT_OPTS = {
  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
};

/** Escape a value for an iCalendar text field. */
function icsEscape(s) {
  return String(s || '').replace(/([\\;,])/g, '\\$1').replace(/\n/g, '\\n');
}

/** Format an ISO timestamp as an iCalendar UTC date-time (YYYYMMDDTHHMMSSZ). */
function icsDate(iso) {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}`
    + `T${p(d.getUTCHours())}${p(d.getUTCMinutes())}00Z`;
}

/** Build an .ics calendar from the booked (timed) fixtures, 1h each. */
function buildIcs(rows) {
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//cs2-battle-stack//leagues//EN'];
  rows.filter((r) => r.scheduled_at).forEach((r) => {
    const start = icsDate(r.scheduled_at);
    const end = icsDate(new Date(new Date(r.scheduled_at).getTime() + 3600000).toISOString());
    lines.push(
      'BEGIN:VEVENT',
      `UID:${r.id}@cs2-battle-stack`,
      `DTSTAMP:${start}`,
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${icsEscape(`${r.league_name}: ${r.home_name} vs ${r.away_name}`)}`,
      `DESCRIPTION:${icsEscape(r.division_name || '')}`,
      'END:VEVENT',
    );
  });
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

/**
 * Personal upcoming-fixtures panel for the logged-in player: their unplayed
 * league matches across all leagues (individual and team), booked time first.
 * Offers a client-side .ics download of the booked matches. Hidden when the
 * viewer is anonymous or has no upcoming fixtures.
 */
export default function MyUpcomingMatches() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [rows, setRows] = useState([]);
  const whenFmt = new Intl.DateTimeFormat(currentLocale(), WHEN_FMT_OPTS);

  useEffect(() => {
    if (!isLoggedIn) {
      setRows([]);
      return;
    }
    let active = true;
    fetch(`${API_URL}/api/leagues/my-upcoming`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => active && setRows(Array.isArray(d) ? d : []))
      .catch(() => active && setRows([]));
    return () => {
      active = false;
    };
  }, [isLoggedIn, getAuthHeaders]);

  if (!isLoggedIn || rows.length === 0) return null;

  const hasBooked = rows.some((r) => r.scheduled_at);

  const downloadIcs = () => {
    const blob = new Blob([buildIcs(rows)], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mina-ligamatcher.ics';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="my-upcoming">
      <div className="my-upcoming-head">
        <h2 className="league-section-title">{t('leagues.myUpcoming')}</h2>
        {hasBooked && (
          <button className="btn-subtle" onClick={downloadIcs}>{t('leagues.downloadCalendar')}</button>
        )}
      </div>
      <ul className="my-upcoming-list">
        {rows.map((r) => (
          <li
            key={r.id}
            className="my-upcoming-row"
            onClick={() => navigate(`/leagues/${r.league_id}`)}
          >
            <span className="my-upcoming-league">{r.league_name}</span>
            <span className="my-upcoming-teams">
              <TeamLogo logoUrl={r.home_logo_url} name={r.home_name} />
              {r.home_name} vs {r.away_name}
              <TeamLogo logoUrl={r.away_logo_url} name={r.away_name} />
            </span>
            <span className="my-upcoming-when">
              {r.scheduled_at ? whenFmt.format(new Date(r.scheduled_at)) : t('leagues.notScheduled')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
